import { ChangeDetectorRef, Component, ViewChild, inject } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { Subject, Subscription, filter, finalize, tap } from 'rxjs';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

import { RestaurantSummaryCardsComponent } from 'src/app/shared/components/restaurant/restaurant-summary-cards/restaurant-summary-cards.component';
import { SummaryCard } from 'src/app/shared/interfaces/restaurant-shared.interface';
import {
  PunchesDataResponse,
  ReservationRecord,
  PunchDataItem,
} from '../../../shared/interfaces/restaurant.interface';

@Component({
  selector: 'app-cook-screen',
  imports: [
    MatButtonModule,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    TitleComponent,
    RestaurantSummaryCardsComponent,
    MatIcon,
    QeqPipe,
    LoaderComponent,
    MatPaginatorModule,
    RouterOutlet,
  ],
  templateUrl: './cook-screen.component.html',
  styleUrls: [
    './cook-screen.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class CookScreenComponent {
  constructor(
    private restaurantService: RestaurantService,
    private rolesService: RolesService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private loaderService: LoaderService,
    private qeqService: QeqService,
    private router: Router
  ) {}

  // ===== NUEVO: contadores de "extra" según estado =====
  private extrasAssignedPending = 0; // extras creados hoy (misma-día) en estado PENDING (incluye invitados)
  private extrasConsumed = 0; // extras creados hoy (misma-día) en estado EATEN (incluye invitados)

  // util para contar menús de una reserva (1 empleado + invitados)
  private countMenusOf(reservation: any): number {
    const guests = (reservation.guests || []).filter((g: any) => !g.is_deleted);
    if (guests.length) return 1 + guests.length;
    return 1 + (reservation.guests_number || 0);
  }

  isEditDatePage = false;

  private destroy$ = new Subject<void>();
  private navigationSubscription: Subscription | null = null;
  private subscriptions: Subscription[] = [];
  myroles: any = {}; // Change from array to object

  // Flag to track if QeQ data is loaded
  public qeqDataLoaded = false;

  // Summary cards for the restaurant-summary-cards component
  summaryCards: SummaryCard[] = [];

  // Add dataSource for table data
  dataSource: MatTableDataSource<
    ReservationRecord & {
      date: string;
      guests_number: number | null;
      is_synced?: boolean;
      has_reservation?: boolean;
      reservation_id?: number | null; // <-- añadimos el id de la reserva asociada
    }
  > = new MatTableDataSource<
    ReservationRecord & {
      date: string;
      guests_number: number | null;
      is_synced?: boolean;
      has_reservation?: boolean;
      reservation_id?: number | null;
    }
  >();

  // Display columns for table
  displayedColumns: string[] = [
    'users',
    'id-users',
    'diet-date',
    'consumed',
    'status',
    'guests',
    'celiac',
    'source', // nueva columna
    'acciones',
  ];
  private _snackBar = inject(MatSnackBar);

  openSnackBar(message: string) {
    this._snackBar.open(message, 'Cerrar', {
      duration: 5000,
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  // LiveAnnouncer for accessibility
  private _liveAnnouncer = inject(LiveAnnouncer);

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  // Selected filter (always 'hoy' for cook screen)
  config: any = {
    extra_menus: 0,
  };

  title: string = 'Pantalla de cocina';
  subtitle: string =
    'Gestiona los menús reservados y consumidos por los empleados';

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'kitchen_screen',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      // console.log(
      //   'No user roles found in storage or no permissions for Comedor'
      // );
    }
  }

  searchText: string = '';

  onSearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;

    // For the UI table filtering - only filtering by search text, not by date
    const filterValue = JSON.stringify({
      searchText: this.searchText.toLowerCase(),
    });

    this.dataSource.filter = filterValue;
  }

  // Add variables for statistics
  public reservationStats: {
    num_reservations: number;
    num_pending_reservations?: number;
    num_consumed_reservations?: number;
    num_celiac_reservations: number;
    num_reservations_guests: number;
    num_celiac_guests: number;
    total_reservations: number; // R (sin extras)
    total_celiac_reservations: number;
    total_normal_reservations: number;
  } = {
    num_reservations: 0,
    num_pending_reservations: 0,
    num_consumed_reservations: 0,
    num_celiac_reservations: 0,
    num_reservations_guests: 0,
    num_celiac_guests: 0,
    total_reservations: 0,
    total_celiac_reservations: 0,
    total_normal_reservations: 0,
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngOnInit() {
    this.loadRoles();
    this.getConfig();
    this.loadReservationData();
    this.setupNavigationListener();
  }

  goToAddReservation() {
    // Solo permite añadir si quedan extras sin asignar
    const currentExtra = this.calculateExtraMenus();
    // if (currentExtra <= 0) {
    //   this.openSnackBar(
    //     'No hay menús extra disponibles, no es posible añadir una reserva.'
    //   );
    // } else {
    this.router.navigate(['/home/dining/cook-screen/reservation-form']);
    // }
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Check if we're on the cook screen route
        if (this.router.url === '/home/dining/cook-screen') {
          // Check if we're coming from reservation-form
          if (
            event.url === '/home/dining/cook-screen' &&
            event.urlAfterRedirects === '/home/dining/cook-screen' &&
            this.isEditDatePage
          ) {
            // Reload data when returning from reservation-form
            this.loadReservationData();
          }
          this.isEditDatePage = false;
        } else if (this.router.url.includes('/dining/cook-screen/edit-date')) {
          this.isEditDatePage = true;
        } else if (
          this.router.url.includes('/dining/cook-screen/reservation-form')
        ) {
          this.isEditDatePage = true;
        }

        this.cdr.detectChanges();
      });
  }

  ngAfterViewInit() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      if (property === 'status') {
        const order: Record<string, number> = {
          PENDING: 1,
          EATEN: 2,
          CANCELLED: 3,
        };
        return order[(item.status || '').toUpperCase()] ?? 99;
      }
      return item[property];
    };
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.cdr.detectChanges();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Clean up all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  // Get restaurant configuration
  getConfig() {
    const today = new Date();
    const todayApiDate = this.formatDateForApi(today);

    const configSub = this.restaurantService
      .getRestaurantConfigByDate(todayApiDate)
      .subscribe({
        next: (config) => {
          this.config = config;
        },
        error: (err) => {
          console.error('Error loading restaurant config:', err);
        },
      });

    this.subscriptions.push(configSub);
  }

  // Format date for API (YYYY-MM-DD)
  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Format date from YYYY-MM-DDT00:00:00Z or YYYY-MM-DD to DD/MM/YYYY for display
  formatDate(dateString: string): string {
    if (!dateString) return '';

    // Handle date strings with or without time component
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Set up filter predicate for searching
  setupFilterPredicateFunction() {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      const searchText = filterObj.searchText.toLowerCase();

      // If no search text, return all results
      if (!searchText) {
        return true;
      }

      // Search in employee name and external ID
      const nameMatch =
        data.employee_name?.toLowerCase().includes(searchText) || false;
      const idMatch =
        data.employee_external_id?.toLowerCase().includes(searchText) || false;

      // Add QeQ data filtering
      let qeqMatch = false;

      // Get cached QeQ data
      const cached = this.qeqService['getQeqFromStorage']?.();

      // Find employee by external_id
      const employeeName =
        cached && cached.data
          ? cached.data.find(
              (u: any) => u.personnelnumber === data.employee_external_id
            )?.name || ''
          : '';

      // Check if QeQ name matches search text
      qeqMatch = employeeName.toLowerCase().includes(searchText);

      return nameMatch || idMatch || qeqMatch;
    };
  }

  // ===== NUEVO: menús extra disponibles (stock restante hoy) =====
  calculateExtraMenus(): number {
    // Use the new extra_menus field from config
    const E = Number(this.config?.extra_menus ?? 0);
    return Math.max(0, E - this.extrasConsumed);
  }

  loadReservationData() {
    this.loaderService.showLoader();
    const todayApiDate = this.formatDateForApi(new Date());
    const body = {
      start_date: todayApiDate,
      end_date: todayApiDate,
      include_breakfast: false,
    };

    const reservationsSub = this.restaurantService
      .getPunchesKitchenData(body)
      .subscribe({
        next: (punchesResp: PunchesDataResponse) => {
          let punches = punchesResp?.data || [];
          punches = punches.sort((a, b) =>
            a.created_at.localeCompare(b.created_at)
          );

          // console.log('Punches fetched:', punches);

          const mappedReservations: (ReservationRecord & {
            is_synced?: boolean;
            created_when?: string;
            has_reservation?: boolean;
            is_extra_punch?: boolean;
            reservation_id?: number | null;
          })[] = punches.map((p: PunchDataItem) => {
            const mealType =
              p.punch_type?.toUpperCase() === 'BREAKFAST'
                ? 'BREAKFAST'
                : 'LUNCH';
            const statusUpper = p.is_eaten
              ? 'EATEN'
              : (p.status || '').toUpperCase() === 'EATEN'
              ? 'EATEN'
              : 'PENDING';

            const reservationsArr = Array.isArray(p.reservations)
              ? p.reservations
              : [];

            // Check if reservation was created today
            const createdDate = new Date(p.created_at);
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const isCreatedToday = createdDate >= todayStart;

            // If created today, treat as no reservation (extra punch)
            const hasReservation =
              reservationsArr.length > 0 && !isCreatedToday;
            const reservationId = hasReservation
              ? reservationsArr[0]?.id ?? null
              : null;
            const isExtraPunch = !hasReservation;
            const isCeliac = hasReservation
              ? reservationsArr[0]?.is_celiac ?? false
              : false;

            // Extract guests data from the first reservation
            const guests =
              hasReservation && reservationsArr[0]?.guests
                ? Array.isArray(reservationsArr[0].guests)
                  ? reservationsArr[0].guests
                  : []
                : [];
            const guestsNumber = guests.length;

            return {
              id: p.id,
              employee: p.employee,
              employee_external_id: p.employee_external_id,
              employee_name: p.employee_name,
              date: p.date,
              meal_type: mealType,
              status: statusUpper,
              is_celiac: isCeliac,
              celiac_comment: null,
              guests_number: guestsNumber,
              guests: guests,
              charge: p.charge,
              punch: null,
              is_deleted: false,
              created_at: p.created_at,
              updated_at: p.synced_at as any,
              created_when: p.created_at,
              is_synced: p.is_synced,
              has_reservation: hasReservation,
              is_extra_punch: isExtraPunch,
              reservation_id: reservationId,
            } as any;
          });

          const activeReservations = mappedReservations.filter(
            (r) => !r.is_deleted
          );

          // Solo hoy
          const todaysReservations = activeReservations.filter(
            (r) => r.date.split('T')[0] === todayApiDate
          );

          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);

          // Separaciones
          const withReservation = todaysReservations.filter(
            (r) => r.has_reservation
          );
          const extraPunches = todaysReservations.filter(
            (r) => r.is_extra_punch
          );

          // Reservas creadas antes de hoy => aportan a R
          const preDayReservations = withReservation.filter((r) => {
            const createdDate = new Date(
              (r as any)['created_when'] || r.created_at || r.date
            );
            return createdDate < todayStart;
          });

          // Reservas (con reserva) creadas hoy (last‑minute) también se tratan como extras (mantiene lógica previa)
          const sameDayNewReservations = withReservation.filter((r) => {
            const createdDate = new Date(
              (r as any)['created_when'] || r.created_at || r.date
            );
            return createdDate >= todayStart;
          });

          // Menús reservados (R) (solo reservas previas al día)
          const employeeMenus = preDayReservations.length;
          const employeeCeliacMenus = preDayReservations.filter(
            (r) => r.is_celiac
          ).length;

          // Count guests from pre-day reservations (only these count towards R)
          const guestAggregation = preDayReservations.reduce(
            (acc, r) => {
              const guests = r.guests || [];
              const activeGuests = guests.filter((g: any) => !g.is_deleted);
              acc.total += activeGuests.length;
              acc.celiac += activeGuests.filter((g: any) => g.is_celiac).length;
              return acc;
            },
            { total: 0, celiac: 0 }
          );
          const R = employeeMenus + guestAggregation.total;

          // Total expected guests from all pre-day reservations
          const expectedGuestsCount = guestAggregation.total;

          // Extras planificados: use new extra_menus field
          const E = Number(this.config?.extra_menus ?? 0);

          // Count guest punches (employee_external_id "00000") that have been eaten
          const guestPunches = todaysReservations.filter(
            (r) => r.employee_external_id === '00000'
          );
          const guestPunchesEaten = guestPunches.filter(
            (r) => r.status === 'EATEN'
          );

          // Only count guest punches that exceed expected guests as consumed extras
          const excessGuestPunches = Math.max(
            0,
            guestPunchesEaten.length - expectedGuestsCount
          );

          // Conjunto que cuenta como extras: punches sin reserva (excluding guests within limit) + reservas creadas hoy
          const extraPunchesNonGuest = extraPunches.filter(
            (r) => r.employee_external_id !== '00000'
          );
          const extrasUniverse = [
            ...extraPunchesNonGuest,
            ...sameDayNewReservations,
          ];

          const extrasEatenMenus =
            extrasUniverse
              .filter((r) => r.status === 'EATEN')
              .reduce((sum, r) => sum + this.countMenusOf(r), 0) +
            excessGuestPunches;

          this.extrasConsumed = extrasEatenMenus;

          const extrasSinAsignar = Math.max(0, E - this.extrasConsumed);

          // Pendientes / consumidos normales (solo reservas previas al día)
          const pendientesNormales = preDayReservations
            .filter((r) => r.status === 'PENDING')
            .reduce((sum, r) => sum + this.countMenusOf(r), 0);

          const consumidosNormales = preDayReservations
            .filter((r) => r.status === 'EATEN')
            .reduce((sum, r) => sum + this.countMenusOf(r), 0);

          const pendientesTotal = pendientesNormales + extrasSinAsignar;
          const consumidosTotal = consumidosNormales + this.extrasConsumed;

          const menusTotales = R + E;
          if (pendientesTotal + consumidosTotal !== menusTotales) {
            console.warn('Descuadre cálculos cocina', {
              R,
              E,
              pendientesTotal,
              consumidosTotal,
              menusTotales,
            });
          }

          const totalMenus = R;
          const totalCeliacMenus =
            employeeCeliacMenus + guestAggregation.celiac;
          const totalNormalMenus = totalMenus - totalCeliacMenus;

          this.reservationStats = {
            num_reservations: employeeMenus,
            num_pending_reservations: pendientesTotal,
            num_consumed_reservations: consumidosTotal,
            num_celiac_reservations: employeeCeliacMenus,
            num_reservations_guests: guestAggregation.total,
            num_celiac_guests: guestAggregation.celiac,
            total_reservations: totalMenus,
            total_celiac_reservations: totalCeliacMenus,
            total_normal_reservations: totalNormalMenus,
          } as typeof this.reservationStats;

          // Tabla: todos los punches de hoy
          const formattedReservations = todaysReservations.map((r) => ({
            ...r,
            date: this.formatDate(r.date),
            guests_number:
              r.guests_number ??
              ((r.guests || []).filter((g: any) => !g.is_deleted).length || 0),
          }));

          this.dataSource.data = formattedReservations as any;
          this.setupFilterPredicateFunction();
          if (this.sort) this.dataSource.sort = this.sort;

          (this as any).__R = R;
          (this as any).__E = E;
          (this as any).__extrasSinAsignar = extrasSinAsignar;
          (this as any).__pendientesNormales = pendientesNormales;
          (this as any).__consumidosNormales = consumidosNormales;

          this.cdr.detectChanges();
          this.loaderService.hideLoader();
          this.updateSummaryCards();
        },
        error: (error) => {
          this.loaderService.hideLoader();
          console.error('Error loading punches:', error);
          this.openSnackBar('Error al cargar datos');
        },
      });

    this.subscriptions.push(reservationsSub);
  }

  // Update summary cards with current reservation stats
  updateSummaryCards() {
    // R y E calculados en loadReservationData
    const R = (this as any).__R || 0;
    const E = (this as any).__E || 0;

    const extrasSinAsignar = (this as any).__extrasSinAsignar || 0;
    const extrasConsumidos = this.extrasConsumed || 0;

    const pendientes = this.reservationStats.num_pending_reservations || 0;
    const consumidos = this.reservationStats.num_consumed_reservations || 0;
    const menusTotales = R + E;

    const displayReservations = this.reservationStats.total_reservations; // = R
    const displayEmployees = this.reservationStats.num_reservations;
    const displayGuests = this.reservationStats.num_reservations_guests;

    this.summaryCards = [
      {
        title: 'MENÚS TOTALES RESERVADOS',
        value: displayReservations, // R (sin extra)
        subtitle: `Empleados: ${displayEmployees} | Invitados: ${displayGuests}`,
      },
      {
        title: 'MENÚS NORMALES RESERVADOS',
        value: this.reservationStats.total_normal_reservations,
        subtitle: `Empleados: ${
          this.reservationStats.num_reservations -
          this.reservationStats.num_celiac_reservations
        } | Invitados: ${
          this.reservationStats.num_reservations_guests -
          this.reservationStats.num_celiac_guests
        }`,
      },
      {
        title: 'MENÚS CELÍACOS RESERVADOS',
        value: this.reservationStats.total_celiac_reservations,
        subtitle: `Empleados: ${this.reservationStats.num_celiac_reservations} | Invitados: ${this.reservationStats.num_celiac_guests}`,
      },
      {
        title: 'MENÚS EXTRA',
        value: E,
        subtitle: `Disponibles: ${extrasSinAsignar} | Consumidos: ${extrasConsumidos}`,
      },
      {
        title: 'MENÚS TOTALES',
        value: menusTotales, // R + E
        subtitle: `Pendientes: ${pendientes} | Consumidos: ${consumidos}`,
      },
    ];
  }

  // Method to view details of a reservation
  viewDetails(id: number) {
    if (id == null) return;
    this.router.navigate(['/home/dining/cook-screen/edit-date', id]);
  }

  // Method to delete a reservation
  deleteSelected(id: number, title: string) {
    //   console.log('Delete reservation', id, title);
  }

  refreshReservations() {
    this.loaderService.showLoader(); // Show loader while refreshing
    const todayDate = this.formatDateForApi(new Date());

    this.restaurantService
      .syncPunchesKitchen(todayDate, todayDate)
      .pipe(
        tap((response) => {}),
        finalize(() => {
          // This will run after the observable completes or errors
          this.loadReservationData();
          this.openSnackBar('Reservas actualizadas correctamente');
        })
      )
      .subscribe({
        error: (error) => {
          console.error('Error refreshing reservations:', error);
          this.openSnackBar('Error al actualizar reservas');
        },
      });
  }
}
